import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { appleCalendarUrl, googleCalendarUrl, outlookCalendarUrl } from '../lib/subscribe';

interface Props {
  icsUrl: string;
  caption?: string;
}

type QrTarget = 'apple' | 'google' | 'outlook';
type Platform = 'ios' | 'android' | 'desktop';

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

export function SubscribeButtons({ icsUrl, caption }: Props) {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [qrTarget, setQrTarget] = useState<QrTarget>('apple');

  const platform = detectPlatform();
  const isMobile = platform !== 'desktop';

  const qrValue =
    qrTarget === 'apple'
      ? icsUrl.replace(/^https?:\/\//, 'webcal://')
      : qrTarget === 'google'
        ? googleCalendarUrl(icsUrl)
        : outlookCalendarUrl(icsUrl);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(icsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* no-op */
    }
  };

  return (
    <div>
      {caption && <p className="muted" style={{ margin: '0 0 0.5rem' }}>{caption}</p>}

      {platform === 'ios' && (
        <>
          {/* Apple first — webcal:// opens the Calendar app directly, no browser needed. */}
          <div className="subscribe-grid">
            <LinkButton href={appleCalendarUrl(icsUrl)} label="Apple" external={false} />
            <LinkButton href={googleCalendarUrl(icsUrl)} label="Google" />
            <LinkButton href={outlookCalendarUrl(icsUrl)} label="Outlook" />
          </div>
          <p className="soft" style={{ marginTop: '0.5rem' }}>
            Apple opens directly in Apple Calendar. Google and Outlook open in your browser — sign in first.
          </p>
        </>
      )}

      {platform === 'android' && (
        <>
          {/* Apple Calendar doesn't exist on Android. webcal:// is not handled by Chrome,
              so only show apps that work via the web flow. */}
          <div className="subscribe-grid subscribe-grid-2">
            <LinkButton href={googleCalendarUrl(icsUrl)} label="Google" />
            <LinkButton href={outlookCalendarUrl(icsUrl)} label="Outlook" />
          </div>
          <p className="soft" style={{ marginTop: '0.5rem' }}>
            Opens in your browser — sign in to Google or Outlook first.
          </p>
        </>
      )}

      {platform === 'desktop' && (
        <div className="subscribe-grid">
          <LinkButton href={googleCalendarUrl(icsUrl)} label="Google" />
          <LinkButton href={appleCalendarUrl(icsUrl)} label="Apple" external={false} />
          <LinkButton href={outlookCalendarUrl(icsUrl)} label="Outlook" />
        </div>
      )}

      <p className="soft" style={{ marginTop: '0.5rem' }}>
        Using another app? Paste this URL into "Add calendar from URL"
        {!isMobile && (
          <>
            , or{' '}
            <button
              type="button"
              className="inline-link"
              aria-pressed={showQr}
              aria-expanded={showQr}
              onClick={() => setShowQr((v) => !v)}
            >
              {showQr ? 'hide QR code' : 'use QR code'}
            </button>
          </>
        )}
        .
      </p>

      <div className="copy-row">
        <div className="copy-field" title={icsUrl} aria-label="Calendar subscription URL">{icsUrl}</div>
        <button
          type="button"
          className="copy-inline"
          onClick={copy}
          aria-label={copied ? 'URL copied to clipboard' : 'Copy URL to clipboard'}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {!isMobile && showQr && (
        <div className="qr-block">
          <div className="qr">
            <QRCodeSVG value={qrValue} size={140} level="M" includeMargin={false} />
          </div>
          <div className="segment">
            {(['apple', 'google', 'outlook'] as const).map((t) => (
              <button
                key={t}
                type="button"
                className={qrTarget === t ? 'active' : ''}
                aria-pressed={qrTarget === t}
                onClick={() => setQrTarget(t)}
              >
                {t === 'apple' ? 'Apple' : t === 'google' ? 'Google' : 'Outlook'}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LinkButton({ href, label, external = true }: { href: string; label: string; external?: boolean }) {
  return (
    <a
      href={href}
      className="subscribe-link"
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {label}
    </a>
  );
}
